// Strategy metadata for the backtesting picker (U3 / R2). Authored retail copy — NOT extracted from the
// backend's terse implementation-facing doc comments. Each entry is written to a rubric:
//   - blurb: <= 15 words, what the user sees in trader terms (not internal mechanics)
//   - when:  a one-line market-regime cue ("best in trending markets")
//   - detail: a fuller plain-language explanation shown in the picker's accordion on selection
// `id` is the backend StrategyType enum int (used verbatim on the request). Custom=7 is a no-op authoring
// template and is deliberately excluded — the picker shows every other StrategyType member (keep this
// catalog in lockstep with the enum; the count guard lives in StrategyModelsTests).
//
// Per-strategy tuning IS now exposed (KTD5): the backtest request carries an optional `Tuning` object and
// the form renders the relevant knobs per strategy (see the tuning schema at the bottom of this file).
// Every knob is optional — omit it and the strategy uses its tuned default (byte-identical to v1).
//
// POLICY DOES NOT LIVE HERE (ADR-0024 §2). Which parameters a strategy freezes, and whether it selects
// its own universe, come from `GET /api/strategies` via lib/strategyPolicy.js — the strategy that
// forces a parameter is the thing that knows it does. This file holds the cosmetic catalog (names,
// blurbs, which knobs exist) only; adding a frozen strategy here does NOT require touching lock rules.

export const strategies = [
  {
    id: 1,
    key: 'SimpleMovingAvg',
    name: 'Simple Moving Average',
    blurb: 'Buys when the short average crosses above the long one, sells when it crosses back.',
    when: 'Best in steady, trending markets with low noise.',
    detail:
      'Tracks two moving averages of price — a fast one and a slow one. When the fast average rises ' +
      'above the slow average it reads as upward momentum and goes long; when it falls back below it ' +
      'exits. A classic trend-following starting point: simple, transparent, and easy to reason about.',
  },
  {
    id: 2,
    key: 'LinearRegression',
    name: 'Linear Regression Trend',
    blurb: 'Fits a trend line through recent prices and trades in the direction of its slope.',
    when: 'Best when a market is trending cleanly in one direction.',
    detail:
      'Draws a best-fit line through a window of recent prices and reads its slope. A rising slope signals ' +
      'a strengthening uptrend and opens a long; a flattening or falling slope closes it. Smooths out ' +
      'single-bar noise better than a raw price cross.',
  },
  {
    id: 3,
    key: 'BreakoutTrendFilter',
    name: 'Breakout with Trend Filter',
    blurb: 'Buys breakouts above recent highs, but only while the broader trend agrees.',
    when: 'Best in markets that move in sustained breakouts, not chop.',
    detail:
      'Watches for price breaking above a recent range high and enters on the breakout — but only when a ' +
      'longer-term trend filter confirms the move is with the prevailing direction. The filter is meant to ' +
      'cut down on false breakouts during sideways, choppy conditions.',
  },
  {
    id: 4,
    key: 'LongOnlyMomentum',
    name: 'Long-Only Momentum',
    blurb: 'Holds assets showing the strongest recent gains; never sells short.',
    when: 'Best in bull markets where winners keep winning.',
    detail:
      'Ranks assets by their recent price momentum and holds the strongest performers, rotating as ' +
      'leadership changes. It only ever goes long — it sits out rather than betting against a falling ' +
      'market. Tends to do well when strong trends persist and poorly in sharp reversals.',
  },
  {
    id: 5,
    key: 'SystematicMomentum',
    name: 'Systematic Momentum',
    blurb: 'Goes long strong performers and short weak ones on a fixed, rules-based schedule.',
    when: 'Best across a basket of assets with dispersed performance.',
    detail:
      'A rules-driven momentum approach across multiple symbols: it buys the recent outperformers and can ' +
      'short the underperformers, rebalancing on a set cadence with no discretionary calls. Diversifying ' +
      'across many names is meant to smooth the ride versus a single-asset bet.',
  },
  {
    id: 6,
    key: 'BuyAndHold',
    name: 'Buy and Hold',
    blurb: 'Buys at the start of the window and simply holds to the end.',
    when: 'A baseline benchmark for every other strategy.',
    detail:
      'The simplest possible approach: invest at the beginning of the period and hold without trading. It ' +
      'earns no points for cleverness, but it is the honest yardstick — any active strategy worth running ' +
      'should be able to justify itself against just holding.',
  },
  {
    id: 8,
    key: 'ScalableMovingAvg',
    name: 'Scalable Moving Average',
    blurb: 'A moving-average crossover that sizes positions by trend conviction.',
    when: 'Best in trending markets when you want graded exposure.',
    detail:
      'Builds on the moving-average crossover but, instead of an all-in / all-out switch, scales position ' +
      'size with how strongly the averages agree. A mild signal takes a small position; a strong, ' +
      'persistent trend takes a larger one. Aims for smoother equity than a binary entry.',
  },
  {
    id: 9,
    key: 'ScalableIntraDay',
    name: 'Scalable Intraday',
    blurb: 'Trades shorter-term moves through the day, scaling size with signal strength.',
    when: 'Best on higher-frequency data (minutes to hours), not daily.',
    detail:
      'Designed for intraday timeframes: it reacts to shorter-term price swings and scales each position ' +
      'by signal conviction. Pair it with a fine candle frequency (1m–1h) — on daily candles it has too ' +
      'few bars to work with.',
  },
  {
    id: 10,
    key: 'EWMA',
    name: 'Exponential Moving Average',
    blurb: 'Like the moving-average strategy, but reacts faster to recent price changes.',
    when: 'Best when you want quicker response to turning points.',
    detail:
      'Uses exponentially-weighted averages that put more weight on recent prices, so the strategy turns ' +
      'faster when the market changes direction than a plain simple-average crossover. The trade-off is ' +
      'more sensitivity to short-term noise.',
  },
  {
    id: 11,
    key: 'Shishito',
    name: 'Shishito Factor Book',
    blurb: 'Holds up to 25 crypto names picked hourly by a five-factor rank, long-only.',
    when: 'Best run on hourly candles over a broad crypto universe including BTC-USD.',
    detail:
      'A research-audited, long-only crypto factor strategy: every hour it scores the universe on ' +
      'momentum, risk-adjusted trend, volume surge, volatility and proximity to highs, holds the top ' +
      'names when the market regime is healthy, and exits on stops, time limits or factor decay. Its ' +
      'parameters are frozen to match the audited research — there are no tuning knobs, and BTC-USD ' +
      'must be part of the deployed symbols (it drives the market-regime gate).',
  },
  {
    id: 12,
    key: 'Kcp1',
    name: 'KCP-1 BTC Convexity Sleeve',
    blurb: 'Holds BTC when two trend conditions agree, cash otherwise. Long/cash, BTC-USD only.',
    when: 'Deploy on daily candles with BTC-USD as the ONLY symbol (any other configuration will not trade); warm-up history is fetched automatically.',
    detail:
      'A research-audited long/cash rule on BTC-USD spot: long when the 5-day EWMA is above the ' +
      '40-day EWMA and the close is above the 200-day EWMA, cash otherwise — evaluated once per daily ' +
      'close. The claim is convexity, not alpha: it pays premium in trendless markets and pays out in ' +
      'BTC down-markets. Parameters are frozen to match the audited research — no tuning knobs, no ' +
      'shorting, no leverage, exactly one instrument (a multi-symbol deployment will not trade).',
  },
];

// Default selection: an approachable, valid enum member (the API default 0 is invalid — KTD5).
export const defaultStrategyId = 1; // SimpleMovingAvg

export function strategyById(id) {
  return strategies.find((s) => s.id === id) || null;
}

// Look up a strategy by its persisted enum KEY (e.g. "SimpleMovingAvg") — used to render a saved run's
// strategy name in the run-parameters panel, where the result carries the enum name, not the numeric id.
export function strategyByKey(key) {
  return strategies.find((s) => s.key === key) || null;
}

// ---------------------------------------------------------------------------------------------------
// Per-strategy tuning schema (KTD5). The backend now accepts an optional `Tuning` object on the
// backtest request (BacktestRequest.Tuning -> K2.Strategies.Models.StrategyTuning). Every field is
// OPTIONAL: omit it and the strategy falls back to its own default / seeding (byte-identical to before).
//
// We only surface the knobs a given strategy actually reads, so the form stays uncluttered. The catalog
// below is authored from the strategy bodies' field reads:
//   SimpleMovingAvg/Breakout -> short/long MA (+ breakout trigger, trend filter)
//   LongOnlyMomentum/SystematicMomentum -> RSI/return/stddev windows, top-N, momentum threshold
//   Scalable*/EWMA -> MA period list(s), warmup
//
// Field `kind`:
//   'int'      -> number input; sent as a plain integer
//   'intlist'  -> comma-separated integers -> number[]  (MovingAvgPeriods)
//   'pairlist' -> "short:long, short:long" -> number[][] (ShortLongMovingAvgPeriods)
// `key` is the exact StrategyTuning PascalCase property name (the host binds case-insensitively, but we
// send PascalCase to match the rest of the request).

// Policy flags apply to every strategy (the operator-wins overrides). Collapsed by default in the UI so
// they don't clutter the common path. `kind: 'enum'` carries its own options.
export const policyFlags = [
  {
    key: 'ReverseTradeSignal',
    kind: 'bool',
    label: 'Reverse signals',
    hint: 'Invert every buy/sell — trade the opposite of what the strategy says.',
  },
  {
    key: 'AllowShortTrading',
    kind: 'bool',
    label: 'Allow short selling',
    hint: 'Permit short positions, not just long. Off keeps the strategy long-only.',
  },
  {
    key: 'IsolateFunds',
    kind: 'bool',
    label: 'Isolate funds',
    hint: 'Ring-fence this strategy’s capital so it does not draw on the shared balance.',
  },
  {
    key: 'TradeOnStart',
    kind: 'bool',
    label: 'Trade on start',
    hint: 'Open a position immediately on the first bar instead of waiting for a fresh signal.',
  },
  {
    key: 'RebalanceFrequency',
    kind: 'enum',
    label: 'Rebalance cadence',
    hint: 'How often holdings are rebalanced toward target weights.',
    // RebalanceEventFrequency enum ints: None=0, AtFrequency=1, Daily=2, Weekly=3, Monthly=4, Quarterly=5, Annual=6
    options: [
      { value: 0, label: 'None' },
      { value: 1, label: 'Every cycle' },
      { value: 2, label: 'Daily' },
      { value: 3, label: 'Weekly' },
      { value: 4, label: 'Monthly' },
      { value: 5, label: 'Quarterly' },
      { value: 6, label: 'Annual' },
    ],
  },
];

// Reusable numeric param descriptors, keyed by StrategyTuning property.
const PARAM = {
  shortMa: { key: 'ShortTermMovingAvg', kind: 'int', label: 'Short MA period', hint: 'Bars in the fast moving average.', min: 1, max: 100000, placeholder: '5' },
  longMa: { key: 'LongTermMovingAvg', kind: 'int', label: 'Long MA period', hint: 'Bars in the slow moving average.', min: 1, max: 100000, placeholder: '40' },
  breakout: { key: 'BreakoutTrigger', kind: 'int', label: 'Breakout lookback', hint: 'Bars of range high/low that define a breakout.', min: 1, max: 100000, placeholder: '40' },
  warmup: { key: 'MinHistoricalPeriod', kind: 'int', label: 'Warmup bars', hint: 'Minimum history before trading begins.', min: 1, max: 100000, placeholder: 'auto' },
  rsi: { key: 'RSIPeriod', kind: 'int', label: 'RSI period', hint: 'Lookback for the RSI filter.', min: 1, max: 100000, placeholder: '14' },
  stddev: { key: 'StdDeviationPeriod', kind: 'int', label: 'Std-dev period', hint: 'Lookback for volatility sizing.', min: 1, max: 100000, placeholder: '21' },
  returnP: { key: 'ReturnPeriod', kind: 'int', label: 'Return period', hint: 'Lookback used to rank momentum.', min: 1, max: 100000, placeholder: '63' },
  momentum: { key: 'MinMomentumScore', kind: 'int', label: 'Min momentum score', hint: 'Minimum score to admit a symbol (may be 0 or negative).', placeholder: '0' },
  topN: { key: 'TopSymbolsToTrade', kind: 'int', label: 'Top symbols to trade', hint: 'How many of the strongest names to hold.', min: 1, max: 100000, placeholder: '5' },
  topCap: { key: 'TopMarketCapPairsLimit', kind: 'int', label: 'Market-cap universe', hint: 'Cap on the top-market-cap universe scanned.', min: 1, max: 100000, placeholder: '20' },
  maList: { key: 'MovingAvgPeriods', kind: 'intlist', label: 'MA periods', hint: 'Comma-separated, e.g. 2, 8, 32 (max 64).', placeholder: '2, 8, 32' },
  pairList: { key: 'ShortLongMovingAvgPeriods', kind: 'pairlist', label: 'Short/long MA pairs', hint: 'short:long pairs, e.g. 2:8, 4:16 (max 64, short < long).', placeholder: '2:8, 4:16, 8:32' },
};

// Map StrategyType id -> the numeric params that strategy actually reads.
const STRATEGY_PARAMS = {
  1: [PARAM.shortMa, PARAM.longMa], // SimpleMovingAvg
  2: [], // LinearRegression — no numeric knobs on the strategy path (defaults)
  3: [PARAM.shortMa, PARAM.longMa, PARAM.breakout], // BreakoutTrendFilter
  4: [PARAM.rsi, PARAM.returnP, PARAM.stddev, PARAM.topN, PARAM.topCap], // LongOnlyMomentum
  5: [PARAM.returnP, PARAM.topN, PARAM.topCap, PARAM.momentum, PARAM.warmup], // SystematicMomentum
  6: [], // BuyAndHold
  8: [PARAM.maList, PARAM.warmup], // ScalableMovingAvg
  9: [PARAM.warmup], // ScalableIntraDay
  10: [PARAM.pairList, PARAM.warmup], // EWMA
  11: [], // Shishito — frozen research spec: no tunable knobs (ADR-0016)
  12: [], // KCP-1 — frozen research spec: no tunable knobs at all (TASK-20260728-kcp1-convexity-sleeve)
};

// Flags a strategy accepts but IGNORES. This is not the same fact as "the boundary rejects it" —
// rejected/forced parameters now come from the server's policy declaration and render locked with their
// forced value (ADR-0024 §2, lib/strategyPolicy.js). An inert flag has nothing to display: the request
// would be accepted and simply do nothing, so hiding it remains the honest treatment.
// Shishito and KCP-1 declare ReverseTradeSignal deliberately inert (reversing a frozen long-only book
// would mean shorting — STRATEGY_AUTHORING.md §1).
const INERT_POLICY_FLAGS = {
  11: ['ReverseTradeSignal'],
  12: ['ReverseTradeSignal'],
};

/** The shared policy flags worth showing for a strategy id (flags it would silently ignore are hidden). */
export function policyFlagsFor(id) {
  const inert = INERT_POLICY_FLAGS[id] ?? [];
  return policyFlags.filter((f) => !inert.includes(f.key));
}

// Strategy-specific boolean toggles that aren't part of the shared policy set (read by exactly one
// strategy and naturally belong inline with its numeric params).
const STRATEGY_FLAGS = {
  3: [{ key: 'IsTrendFilterOff', kind: 'bool', label: 'Disable trend filter', hint: 'Take breakouts even when the longer-term trend disagrees.' }],
  9: [{ key: 'IsIntraday', kind: 'bool', label: 'Intraday mode', hint: 'Treat the strategy as intraday (positions close at session end).' }],
  4: [{ key: 'TakeTopMarketCapSymbols', kind: 'bool', label: 'Top market-cap only', hint: 'Restrict the universe to the largest-cap symbols.' }],
  5: [{ key: 'TakeTopMarketCapSymbols', kind: 'bool', label: 'Top market-cap only', hint: 'Restrict the universe to the largest-cap symbols.' }],
};

/** Numeric + strategy-specific-flag params for a strategy id (the inline, always-visible knobs). */
export function strategyParams(id) {
  return [...(STRATEGY_PARAMS[id] ?? []), ...(STRATEGY_FLAGS[id] ?? [])];
}

/** Parse a UI tuning-form object (string values keyed by param key) into the request `Tuning` shape.
 *  Only non-empty fields are included; an all-empty form yields null (no overrides — byte-identical). */
export function buildTuning(formValues) {
  const tuning = {};

  const intlistParse = (raw) =>
    raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0).map(Number);

  for (const [key, raw] of Object.entries(formValues)) {
    if (raw === '' || raw === null || raw === undefined) continue;

    const descriptor = findDescriptor(key);
    if (!descriptor) continue;

    switch (descriptor.kind) {
      case 'int': {
        const n = Number(raw);
        if (!Number.isNaN(n)) tuning[key] = Math.trunc(n);
        break;
      }
      case 'enum': {
        const n = Number(raw);
        if (!Number.isNaN(n)) tuning[key] = n;
        break;
      }
      case 'bool': {
        if (raw === true) tuning[key] = true;
        else if (raw === false) {
          // Only send an explicit false when the user actively unset a default-on flag; the form tracks
          // touched flags separately (see StrategyParameters), so here a literal false is intentional.
          tuning[key] = false;
        }
        break;
      }
      case 'intlist': {
        const arr = intlistParse(raw).filter((n) => !Number.isNaN(n));
        if (arr.length > 0) tuning[key] = arr;
        break;
      }
      case 'pairlist': {
        const pairs = raw
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
          .map((p) => p.split(':').map((x) => Number(x.trim())))
          .filter((pair) => pair.length === 2 && pair.every((n) => !Number.isNaN(n))); // not-a-venue-id: a [lo, hi] pair
        if (pairs.length > 0) tuning[key] = pairs;
        break;
      }
    }
  }

  return Object.keys(tuning).length > 0 ? tuning : null;
}

function findDescriptor(key) {
  for (const p of Object.values(PARAM)) if (p.key === key) return p;
  for (const f of policyFlags) if (f.key === key) return f;
  for (const list of Object.values(STRATEGY_FLAGS)) {
    const hit = list.find((f) => f.key === key);
    if (hit) return hit;
  }
  return null;
}
